import React, { Fragment, useState, useMemo, useEffect, useRef } from 'react'
import { Link } from 'gatsby'
import classnames from 'classnames'
import { navigate } from 'gatsby'
import queryString from 'query-string'
import formatDate from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import { Columns, Column } from 'react-flex-columns'
import VisuallyHidden from '@reach/visually-hidden'
import Alert from '@reach/alert'
import { calculateCartTotals } from './utils'
import Notice from 'shared/ui/Notice'
import Heading from 'shared/ui/Heading'
import Icon from 'shared/ui/Icon'
import DataTable from 'shared/ui/DataTable'
import FocusedHeader from './FocusedHeader'

let firstRender = true

export default function SelectTickets({
  context: { workshopData, ticketSelectionError, ticketsToPurchase: prevTicketsToPurchase },
  send,
}) {
  useEffect(() => {
    if (firstRender) {
      firstRender = false
    }
  }, [])

  const initialTicketsToPurchase = useMemo(() => {
    if (prevTicketsToPurchase) return prevTicketsToPurchase
    let ticketsToPurchase = {}
    workshopData.ticketTypes.forEach((type) => {
      ticketsToPurchase[type.name] = 0
    })
    return ticketsToPurchase
  }, [workshopData, prevTicketsToPurchase])

  const [submitCartEmpty, setSubmitCartEmpty] = useState(false)
  const [ticketsToPurchase, setTicketsToPurchase] = useState(initialTicketsToPurchase)
  const cartTotals = calculateCartTotals(workshopData, ticketsToPurchase)
  const afterPurchaseTotalAvailable = workshopData.seatsAvailable - cartTotals.quantity

  // If a promo code was sent to the server but we got no promo tickets back
  const invalidPromoCode =
    workshopData.promoCodeTried &&
    !workshopData.ticketTypes.find(
      (type) => type.name.toLowerCase() === workshopData.promoCodeTried.toLowerCase()
    )

  const handleQuantity = (type, quantity) => {
    const ticketTypeQuantityToPurchase = ticketsToPurchase[type.name]
    const maxSeats =
      workshopData.seatsAvailable - cartTotals.quantity + ticketTypeQuantityToPurchase
    const hasTypeAvailability = type.available != null

    // set nextQuantity to what the user wants, or the max possible for
    // that ticket type
    let nextQuantity = quantity <= maxSeats ? quantity : maxSeats
    if (hasTypeAvailability) {
      nextQuantity = Math.min(type.available, nextQuantity)
    }

    const next = {
      ...ticketsToPurchase,
      [type.name]: nextQuantity,
    }

    setTicketsToPurchase(next)
  }

  const query = queryString.parse(window.location.search)
  const promoRef = useRef(query)
  function updateUrlPromo() {
    const newQuery = queryString.stringify({ ...query, promo: promoRef.current.value.trim() })
    navigate(`${window.location.pathname}?${newQuery}`)
    send({ type: 'UPDATE_PROMO_CODE' })
  }

  return (
    <div className="spacing">
      {firstRender ? (
        <Heading as="h1" size={2}>
          Get Tickets
        </Heading>
      ) : (
        <FocusedHeader as="h1" size={2}>
          Get Tickets
        </FocusedHeader>
      )}
      <ul className="text-small">
        <li>
          Do you need to learn more about our{' '}
          <Link to="/diversity">Diversity Program and Scholarship Pricing</Link>?
        </li>
        <li>
          Are you apart of a meetup or tech group? Sometimes we can offer discount codes to tech
          groups. Have your group organizer ask more -{' '}
          <a href="mailto:hello@reacttraining.com">hello@reacttraining.com</a>
        </li>
      </ul>
      <hr />
      {ticketSelectionError && (
        <Notice type="error">
          <p>{ticketSelectionError}</p>
        </Notice>
      )}
      {/* Are there just a few tickets available? */}
      {workshopData.seatsAvailable > 0 &&
        afterPurchaseTotalAvailable <= 5 &&
        afterPurchaseTotalAvailable > 0 && (
          <Notice>
            <p>Only {afterPurchaseTotalAvailable} left!</p>
          </Notice>
        )}
      {/* Or did they just add all the remaining tickets to their cart? */}
      {workshopData.seatsAvailable > 0 && afterPurchaseTotalAvailable === 0 && (
        <Notice>
          <p>You have all the remaining tickets in your cart</p>
        </Notice>
      )}
      {invalidPromoCode && (
        <Notice>
          <p>
            We can't find the promo code <strong>{workshopData.promoCodeTried}</strong> in our
            system. Please contact us at hello@reacttraining.com if you think this is an error on
            our part.
          </p>
        </Notice>
      )}
      <form
        className="spacing"
        onSubmit={(event) => {
          event.preventDefault()
          if (cartTotals.quantity <= 0) {
            setSubmitCartEmpty(true)
          } else {
            send({
              type: 'SUBMIT',
              payload: ticketsToPurchase,
            })
          }
        }}
      >
        <DataTable>
          <tbody>
            {workshopData.ticketTypes.map((type) => {
              const ticketTypeQuantityToPurchase = ticketsToPurchase[type.name]
              const label = type.label

              const ticketTypeAvailable = Math.min(workshopData.seatsAvailable, type.available)
              const ticketTypeExpired = type.expired
              const ticketTypeSoldOut = ticketTypeAvailable === 0

              // What would be available if the cart were purchased as it is right now?
              const afterPurchaseTypeAvailable = type.available - ticketTypeQuantityToPurchase
              const almostSoldOut = !ticketTypeSoldOut && afterPurchaseTypeAvailable <= 7

              let disablePickerMessage = ''
              let preventIncrement = false
              if (workshopData.seatsAvailable === 0 || ticketTypeSoldOut) {
                disablePickerMessage = 'Sold Out'
              } else if (ticketTypeExpired) {
                disablePickerMessage = 'Ticket type is expired'
              } else if (afterPurchaseTotalAvailable <= 0 || afterPurchaseTypeAvailable <= 0) {
                preventIncrement = true
              }

              return (
                <tr key={type.name}>
                  <td
                    className={classnames({
                      strike: !!disablePickerMessage,
                    })}
                  >
                    <div>{label}</div>
                    {type.name === 'earlyBird' && type.expires && (
                      <span className="text-small" style={{ color: '#888' }}>
                        Ends {formatDate(parseISO(type.expires), 'MMM do')}!
                      </span>
                    )}
                  </td>
                  <td
                    className={classnames({
                      strike: !!disablePickerMessage,
                    })}
                  >
                    ${type.price / 100}{' '}
                    {almostSoldOut && !disablePickerMessage && (
                      <strong className="text-small" aria-hidden>
                        ({afterPurchaseTypeAvailable} left)
                      </strong>
                    )}
                  </td>
                  <td className="center">
                    {!!disablePickerMessage ? (
                      <span>{disablePickerMessage}</span>
                    ) : (
                      <QuantityPicker
                        almostSoldOut={almostSoldOut}
                        available={ticketTypeAvailable}
                        label={type.label}
                        preventIncrement={preventIncrement}
                        quantity={ticketTypeQuantityToPurchase}
                        name={type.id ? `${type.name}[${type.id}]` : type.name}
                        onChange={(quantity) => handleQuantity(type, quantity)}
                      />
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>Totals</td>
              <td>${cartTotals.price / 100}</td>
              <td className="center">{cartTotals.quantity}</td>
            </tr>
          </tfoot>
        </DataTable>

        <Columns as="footer" split middle>
          <Column>
            <label htmlFor="select-tickets-promo-code">Promo Code:</label>{' '}
            <input
              id="select-tickets-promo-code"
              type="text"
              size="10"
              ref={promoRef}
              defaultValue={query.promo ? query.promo : ''}
              onKeyPress={(event) => {
                if (event.key === 'Enter') {
                  updateUrlPromo()
                  event.preventDefault()
                }
              }}
            />
          </Column>
          <Column>
            {submitCartEmpty && <p className="text-warn">Please select tickets first</p>}
            <button type="submit" className="button icon-text">
              <span>Next: Register Attendees</span>
              <Icon name="arrow-right" size={2} />
            </button>
          </Column>
        </Columns>
      </form>

      <VisuallyHidden>
        <Alert>
          {cartTotals.quantity} tickets total for {`$${cartTotals.price / 100}`}.
        </Alert>
      </VisuallyHidden>
    </div>
  )
}

// Allows users to click the buttons, use arrow keys, or manual
// enter values into the field
function QuantityPicker({
  onChange,
  label,
  name,
  quantity,
  preventIncrement,
  almostSoldOut,
  available,
}) {
  const inputId = `qty-${label}`

  const [deletedEverything, setDeletedEverything] = useState(false)

  const increment = () => onChange(quantity + 1)
  const decrement = () => onChange(Math.max(0, quantity - 1))

  const handleChange = (event) => {
    const value = parseInt(event.target.value, 10)
    if (isNaN(value)) {
      setDeletedEverything(true)
      onChange(0)
    } else {
      setDeletedEverything(false)
      onChange(value)
    }
  }

  const handleKeyDown = (event) => {
    setDeletedEverything(false)
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      decrement()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      increment()
    }
  }

  const handleBlur = (event) => {
    if (deletedEverything) {
      setDeletedEverything(false)
      onChange(0)
    }
  }

  return (
    <Fragment>
      <VisuallyHidden aria-hidden="true">
        <Alert>
          {quantity} {label}
        </Alert>
      </VisuallyHidden>
      <Columns gutterSize={0.3}>
        <Column className="align-center">
          <button
            aria-label={`Decrease ${label} tickets.`}
            disabled={quantity === 0}
            type="button"
            onClick={decrement}
          >
            <Icon name="subtract-circle" />
          </button>
        </Column>
        <Column className="align-center" flex>
          <input
            id={inputId}
            onChange={handleChange}
            onBlur={handleBlur}
            type="text"
            name={name}
            value={deletedEverything ? '' : quantity}
            style={{
              textAlign: 'center',
              width: '100%',
            }}
            onKeyDown={handleKeyDown}
          />
        </Column>
        <Column className="align-center">
          <button
            aria-label={`Increase ${label} tickets.`}
            type="button"
            disabled={preventIncrement}
            onClick={increment}
          >
            <Icon name="add-circle" color={preventIncrement ? 'grayThree' : 'blue'} />
          </button>
        </Column>
      </Columns>
      <VisuallyHidden>
        <label htmlFor={inputId}>
          <span>{label} tickets.</span> {almostSoldOut && <span>Only {available} left.</span>}
          {name === 'earlyBird' && <span>Ends Sep. 24th.</span>}
        </label>
      </VisuallyHidden>
    </Fragment>
  )
}
